import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Pagination,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
  Card,
  CardContent,
  TableSortLabel,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { strategyService, StrategyExecution } from '../../services/strategyService';

const StrategyResults: React.FC = () => {
  const { executionId } = useParams<{ executionId: string }>();
  const navigate = useNavigate();
  
  const [execution, setExecution] = useState<StrategyExecution | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  
  // Pagination states
  const [resultsPage, setResultsPage] = useState(1);
  const [resultsPerPage, setResultsPerPage] = useState(50);
  const [pageJumpValue, setPageJumpValue] = useState('');
  
  // Sorting states
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string>('symbol');

  useEffect(() => {
    if (executionId) {
      loadStrategyExecution();
    }
  }, [executionId]);

  const loadStrategyExecution = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Get execution details from the strategies list
      const strategies = await strategyService.getStrategies();
      
      // Find the execution in the executions list
      let foundExecution: StrategyExecution | null = null;
      for (const strategy of strategies) {
        if (strategy.executions) {
          foundExecution = strategy.executions.find((exec: StrategyExecution) => exec.id === executionId) || null;
          if (foundExecution) {
            foundExecution.strategy_name = strategy.name;
            break;
          }
        }
      }
      
      if (!foundExecution) {
        setError('Strategy execution not found');
        return;
      }
      
      setExecution(foundExecution);
    } catch (err: any) {
      console.error('Error loading strategy execution:', err);
      setError(err.response?.data?.message || 'Failed to load strategy execution');
      toast.error('Failed to load strategy results');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToStrategies = () => {
    navigate('/strategies');
  };

  const handleViewStock = (symbol: string) => {
    navigate(`/analysis?symbol=${encodeURIComponent(symbol)}`);
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sorting utility functions
  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  function getComparator<Key extends keyof any>(
    order: 'asc' | 'desc',
    orderBy: Key,
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string },
  ) => number {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  const handlePageJump = () => {
    const pageNum = parseInt(pageJumpValue);
    if (pageNum && pageNum > 0 && execution?.results) {
      const maxPage = Math.ceil(execution.results.length / resultsPerPage);
      if (pageNum <= maxPage) {
        setResultsPage(pageNum);
        setPageJumpValue('');
      } else {
        toast.error(`Page number must be between 1 and ${maxPage}`);
      }
    }
  };

  // Helper function to format values
  const formatValue = (value: any, type: string) => {
    if (value === null || value === undefined) return 'N/A';
    
    switch (type) {
      case 'currency':
        return `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
      case 'percentage':
        return `${Number(value).toFixed(2)}%`;
      case 'number':
        return Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 });
      case 'integer':
        return Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 });
      default:
        return String(value);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box px={1} py={0.5}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToStrategies}
          sx={{ mb: 2 }}
        >
          Back to Strategies
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!execution) {
    return (
      <Box px={1} py={0.5}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToStrategies}
          sx={{ mb: 2 }}
        >
          Back to Strategies
        </Button>
        <Alert severity="info">Strategy execution not found</Alert>
      </Box>
    );
  }

  const results = execution.results || [];
  const totalResults = results.length;
  
  // Apply sorting
  const sortedResults = stableSort(results, getComparator(order, orderBy));
  
  const startIndex = (resultsPage - 1) * resultsPerPage;
  const endIndex = Math.min(startIndex + resultsPerPage, totalResults);
  const currentPageResults = sortedResults.slice(startIndex, endIndex);
  const totalPages = Math.ceil(totalResults / resultsPerPage);

  return (
    <Box px={1} py={0.5}>
      {/* Header with Back Button */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Box display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToStrategies}
            sx={{ mr: 2 }}
          >
            Back to Strategies
          </Button>
          <Box>
            <Typography variant="h4" component="h1">
              Strategy Results: {execution.strategy_name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Executed on {execution.started_at ? new Date(execution.started_at).toLocaleString() : 'Unknown'}
            </Typography>
          </Box>
        </Box>
        
        <Box display="flex" alignItems="center" gap={2}>
          <Chip 
            label={`${execution.result_count || 0} Results`} 
            color="primary" 
            variant="outlined" 
          />
          <Chip 
            label={`${execution.changes_detected || 0} Changes`} 
            color={(execution.changes_detected || 0) > 0 ? 'success' : 'default'} 
            variant="outlined" 
          />
          <Chip 
            label={execution.status}
            color={execution.status === 'completed' ? 'success' : 'default'}
          />
        </Box>
      </Box>

      {/* Results Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Execution Summary
          </Typography>
          <Box display="flex" gap={4}>
            <Typography variant="body2">
              <strong>Duration:</strong> {execution.duration_seconds ? `${execution.duration_seconds}s` : 'N/A'}
            </Typography>
            <Typography variant="body2">
              <strong>Status:</strong> {execution.status}
            </Typography>
            <Typography variant="body2">
              <strong>Results:</strong> {execution.result_count || 0} stocks
            </Typography>
            <Typography variant="body2">
              <strong>Changes:</strong> {execution.changes_detected || 0}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Results Table */}
      {results.length > 0 ? (
        <>
          <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    { id: 'name', label: 'Name', align: 'left' },
                    { id: 'last_trade_price', label: 'Price', align: 'right' },
                    { id: 'market_cap', label: 'Market Cap', align: 'right' },
                    { id: 'one_year_return', label: '1Y Return', align: 'right' },
                    { id: 'gco_hit_rate', label: 'GCO Hit Rate', align: 'right' },
                    { id: 'gco_avg_return', label: 'GCO Avg Return', align: 'right' },
                    { id: 'n_swing_10', label: 'N Swing 10', align: 'right' },
                    { id: 'n_swing_15', label: 'N Swing 15', align: 'right' },
                    { id: 'n_swing_20', label: 'N Swing 20', align: 'right' },
                    { id: 'pe_ratio', label: 'PE Ratio', align: 'right' },
                    { id: 'dividend_yield', label: 'Dividend Yield', align: 'right' },
                    { id: 'down_peak_10', label: 'Down Peak 10', align: 'right' },
                    { id: 'down_peak_15', label: 'Down Peak 15', align: 'right' },
                    { id: 'down_peak_20', label: 'Down Peak 20', align: 'right' },
                    { id: 'up_trough_10', label: 'Up Trough 10', align: 'right' },
                    { id: 'up_trough_15', label: 'Up Trough 15', align: 'right' },
                    { id: 'up_trough_20', label: 'Up Trough 20', align: 'right' },
                  ].map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      align={headCell.align as any}
                      sortDirection={orderBy === headCell.id ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(headCell.id)}
                      >
                        {headCell.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {currentPageResults.map((result: any, index: number) => (
                  <TableRow key={result.symbol || index} hover>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        noWrap
                        sx={{ 
                          cursor: 'pointer', 
                          color: 'primary.main',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => handleViewStock(result.symbol || result.Stockname)}
                      >
                        {result.name || result.Stockname || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatValue(result.last_trade_price || result.LastTradePrice, 'currency')}
                    </TableCell>
                    <TableCell align="right">
                      {formatValue(result.market_cap, 'currency')}
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        color={Number(result.one_year_return || result.OneYearReturn) >= 0 ? 'success.main' : 'error.main'}
                      >
                        {formatValue(result.one_year_return || result.OneYearReturn, 'percentage')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatValue(result.gco_hit_rate || result.GCOHitRate, 'percentage')}
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        color={Number(result.gco_avg_return || result.GCOAvgReturn) >= 0 ? 'success.main' : 'error.main'}
                      >
                        {formatValue(result.gco_avg_return || result.GCOAvgReturn, 'percentage')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatValue(result.n_swing_10 || result.NSwing10, 'integer')}
                    </TableCell>
                    <TableCell align="right">
                      {formatValue(result.n_swing_15 || result.NSwing15, 'integer')}
                    </TableCell>
                    <TableCell align="right">
                      {formatValue(result.n_swing_20 || result.NSwing20, 'integer')}
                    </TableCell>
                    <TableCell align="right">
                      {formatValue(result.pe_ratio, 'number')}
                    </TableCell>
                    <TableCell align="right">
                      {formatValue(result.dividend_yield, 'percentage')}
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        color={Number(result.down_peak_10 || result.DownPeak10) < 0 ? 'error.main' : 'textSecondary'}
                      >
                        {formatValue(result.down_peak_10 || result.DownPeak10, 'percentage')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        color={Number(result.down_peak_15 || result.DownPeak15) < 0 ? 'error.main' : 'textSecondary'}
                      >
                        {formatValue(result.down_peak_15 || result.DownPeak15, 'percentage')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        color={Number(result.down_peak_20 || result.DownPeak20) < 0 ? 'error.main' : 'textSecondary'}
                      >
                        {formatValue(result.down_peak_20 || result.DownPeak20, 'percentage')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        color={Number(result.up_trough_10 || result.UpTrough10) > 0 ? 'success.main' : 'textSecondary'}
                      >
                        {formatValue(result.up_trough_10 || result.UpTrough10, 'percentage')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        color={Number(result.up_trough_15 || result.UpTrough15) > 0 ? 'success.main' : 'textSecondary'}
                      >
                        {formatValue(result.up_trough_15 || result.UpTrough15, 'percentage')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        color={Number(result.up_trough_20 || result.UpTrough20) > 0 ? 'success.main' : 'textSecondary'}
                      >
                        {formatValue(result.up_trough_20 || result.UpTrough20, 'percentage')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Controls */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} mt={1}>
            <Typography variant="body2" color="textSecondary">
              Showing {startIndex + 1}-{endIndex} of {totalResults} results
            </Typography>
            
            <Box display="flex" alignItems="center" gap={2}>
              <TextField
                size="small"
                label="Rows per page"
                select
                value={resultsPerPage}
                onChange={(e) => {
                  setResultsPerPage(Number(e.target.value));
                  setResultsPage(1);
                }}
                sx={{ minWidth: 120 }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={500}>500</MenuItem>
              </TextField>
              
              <TextField
                size="small"
                label="Jump to page"
                value={pageJumpValue}
                onChange={(e) => setPageJumpValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handlePageJump()}
                sx={{ width: 120 }}
              />
              <Button size="small" onClick={handlePageJump}>
                Go
              </Button>
              <Pagination
              count={totalPages}
              page={resultsPage}
              onChange={(_, page) => setResultsPage(page)}
              color="primary"
              showFirstButton
              showLastButton
            />
            </Box>
          </Box>
        </>
      ) : (
        <Alert severity="info">
          No results found for this strategy execution.
        </Alert>
      )}
    </Box>
  );
};

export default StrategyResults;